/**
 * The left rail: the room's buckets, as filters with live counts.
 *
 * Same words as v1's agenda — Understand, Solution types, Competitors, Pains,
 * Proposals — but nothing here is a step the room stands on. Every row is a
 * filter over item kinds, clickable at any time, and the count beside it is
 * simply how many there are. Below the buckets, the tag facets the room has
 * grown: pick one and the feed narrows to items carrying it.
 */
import type { ReactNode } from 'react';
import type { BoardroomDetail, BoardroomFilterCount } from '../../types';
import { ValidationFacets } from './LensFacets';
import { VERIFIED_OPTIONS, tagsLabel, type SourcedFilter, VerifiedFilter } from './filters';

export { FOLD_AFTER } from './LensFacets';

export default function FilterRail({
  room,
  filter,
  tags,
  onFilter,
  onTag,
  verified,
  onVerified,
  sourced = null,
  onSourced,
  sourcedCounts = null,
  narrowed = null,
  verifiedCounts = null,
  onClear,
  alsoActive = false,
  lookups = null,
  sorting = null,
}: {
  /** How far the room has got sorting items onto its tags. */
  sorting?: ReactNode;
  // THE LOOKUPS ARE IN BOTH PLACES. The room column shows them always, because
  // this rail is only mounted on the feed and a room looked idle from Reports
  // while eight fetches were in flight; picking one there opens the feed. Here
  // they sit under the Lookups row as filters like any other, so troubleshooting
  // ("show me what failed") happens in the feed without touching the reports.
  /** The lookup kind and state rows, drawn under the Lookups bucket. */
  lookups?: ReactNode;
  /** Drop every axis at once. */
  onClear: () => void;
  /** Delete one dimension:value from every item in the room (the hover ×). */
  /** An axis this rail does not draw is set — the author filter in the strip,
   *  or a lookup kind/state now picked in the room column. */
  alsoActive?: boolean;
  room: BoardroomDetail;
  filter: string | null;
  /** Selected "DIM:value" keys: any of a dimension's, and every dimension. */
  tags: string[];
  onFilter: (key: string | null) => void;
  /** Toggle one value on or off. */
  onTag: (key: string) => void;
  verified: VerifiedFilter | null;
  onVerified: (v: VerifiedFilter | null) => void;
  /** Persona items with no receipt. One chip, beside validation: the same question, asked of provenance. */
  sourced?: SourcedFilter | null;
  onSourced?: (v: SourcedFilter | null) => void;
  sourcedCounts?: Record<SourcedFilter, number> | null;
  /** Counts under the active lens (tag / checked), or null when no lens is on.
   *  Shown in place of the totals, with "x of y" on hover. */
  narrowed?: BoardroomFilterCount[] | null;
  /** Checked chips counted under bucket + tag; null = no counts shown. */
  verifiedCounts?: Record<VerifiedFilter, number> | null;
}) {
  const narrowedByKey = new Map((narrowed ?? []).map(f => [f.key, f.count]));
  const lensLabel = [
    tags.length ? tagsLabel(tags) : null,
    verified ? VERIFIED_OPTIONS.find(o => o.key === verified)?.label ?? verified.toLowerCase() : null,
    sourced === 'UNSOURCED' ? 'unsourced' : sourced === 'SOURCED' ? 'sourced' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const shown = (key: string, total: number) => (narrowed ? narrowedByKey.get(key) ?? 0 : total);
  const hover = (key: string, total: number) =>
    narrowed ? `${narrowedByKey.get(key) ?? 0} of ${total} · ${lensLabel}` : undefined;
  const everythingTotal = room.items_created;
  const everythingShown = narrowed ? narrowed.reduce((n, f) => n + f.count, 0) : everythingTotal;
  const rowClass = (active: boolean) =>
    `w-full flex items-center justify-between px-2 py-1 rounded text-sm text-left transition ${
      active
        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 font-medium'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`;

  return (
    <nav aria-label="Recon filters" className="flex flex-col gap-3 text-sm">
      {(filter || tags.length > 0 || verified || alsoActive) && (
        <button
          type="button"
          onClick={onClear}
          className="mx-2 px-2 py-1 rounded border border-indigo-300 dark:border-indigo-700 text-xs text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
          data-testid="clear-filters"
        >
          Clear filters · show everything
        </button>
      )}
      {/* ABOVE the buckets: this cuts across every kind, so it reads as a lens on
          the list below rather than as one more bucket. */}
      <ValidationFacets
        verified={verified}
        onVerified={onVerified}
        verifiedCounts={verifiedCounts}
        sourced={sourced}
        onSourced={onSourced}
        sourcedCounts={sourcedCounts}
      />

      <div>
        <button
          type="button"
          onClick={() => onFilter(null)}
          className={rowClass(filter === null)}
          title={narrowed ? `${everythingShown} of ${everythingTotal} · ${lensLabel}` : undefined}
        >
          <span>Everything</span>
          <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{everythingShown}</span>
        </button>
        {room.filters.map(f => (
          <div key={f.key}>
            <button
              type="button"
              onClick={() => onFilter(filter === f.key ? null : f.key)}
              className={rowClass(filter === f.key)}
              data-testid={`filter-${f.key}`}
              title={hover(f.key, f.count)}
            >
              <span>{f.label}</span>
              <span className={`text-xs tabular-nums ${narrowed ? 'text-indigo-600 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400'}`}>
                {shown(f.key, f.count)}
              </span>
            </button>
            {f.key === 'lookups' && lookups && <div className="ml-3 mt-0.5">{lookups}</div>}
          </div>
        ))}
      </div>

      {sorting}
      {/* NO TAGS HERE: they have their own tab, and the ones in use show as
          removable chips in the lens bar above the page. */}
    </nav>
  );
}
